import { useEffect, useRef } from 'react';

import { useSelector } from 'hooks';
import { useRecoilValue } from 'recoil';

import type { Event, PlanningEntity } from '../../types';

import { selectedDayPartsAtom } from '../../atoms';

import { Box, styled } from '@mui/material';

import ActivityPanelPragmatic from '../ActivityPanelPragmatic/ActivityPanelPragmatic';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

// Shared empty set for the default prop value. A module-level constant (not an
// inline `new Set()` default) so it isn't reallocated per render and the React
// Compiler can safely reorder it.
const EMPTY_ENTITY_IDS: ReadonlySet<string> = new Set();

const Container = styled(Box)`
  flex: 1 1 100%;
  margin-left: 16px;
  overflow-y: auto;
  height: 100%;
`;

const PanelGrid = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  column-gap: 12px;
  row-gap: 24px;
  padding: 16px;
  padding-right: 16px;
  min-height: 100%;
  box-sizing: border-box;
`;

interface RouteOverviewPragmaticProps {
  activeEntityContainingSelection: string | null;
  planningEntities: PlanningEntity[] | null;
  connectAppointmentGrouping: (appointmentGroupingEntityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  loadingEntityIds?: ReadonlySet<string>;
}

function RouteOverviewPragmatic({
  activeEntityContainingSelection,
  planningEntities = [],
  connectAppointmentGrouping,
  onChangeSelection,
  onChangeActiveEntity,
  loadingEntityIds = EMPTY_ENTITY_IDS
}: RouteOverviewPragmaticProps) {
  'use memo';
  const selectedDayParts = useRecoilValue(selectedDayPartsAtom);
  const { hasActivityPlanningEditAccess } = useSelector(({ grants }) => grants);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    // How many pixels from the top/bottom edge of the container scroll kicks in.
    // The bottom zone is intentionally larger than the top: when dragging down,
    // the cursor sits at the top of the drag preview, so the card's visual
    // bottom is already well below the cursor when it enters the zone.
    const SCROLL_ZONE_TOP_PX = 100;
    const SCROLL_ZONE_BOTTOM_PX = 160;
    // Maximum scroll speed in pixels per frame (at 60fps).
    const MAX_SCROLL_PX_PER_FRAME = 15;

    let rafId: number | null = null;
    let clientY = 0;

    function scrollStep() {
      const rect = el!.getBoundingClientRect();
      const fromTop = clientY - rect.top;
      const fromBottom = rect.bottom - clientY;

      let delta = 0;
      if (fromTop < SCROLL_ZONE_TOP_PX) {
        // Ramp from 0 (at zone boundary) up to MAX_SPEED (at the very edge)
        delta = -MAX_SCROLL_PX_PER_FRAME * (1 - fromTop / SCROLL_ZONE_TOP_PX);
      } else if (fromBottom < SCROLL_ZONE_BOTTOM_PX) {
        delta =
          MAX_SCROLL_PX_PER_FRAME * (1 - fromBottom / SCROLL_ZONE_BOTTOM_PX);
      }

      if (delta !== 0) el!.scrollBy({ top: delta });
      rafId = requestAnimationFrame(scrollStep);
    }

    function stopScroll() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    const cleanupMonitor = monitorForElements({
      onDragStart: ({ location }) => {
        clientY = location.current.input.clientY;
        rafId = requestAnimationFrame(scrollStep);
      },
      onDrag: ({ location }) => {
        clientY = location.current.input.clientY;
      },
      onDrop: stopScroll
    });

    return () => {
      stopScroll();
      cleanupMonitor();
    };
  }, []);

  return (
    <Container ref={scrollContainerRef}>
      <PanelGrid>
        {planningEntities &&
          planningEntities.length > 0 &&
          planningEntities
            ?.filter(
              ({ schedules }) =>
                schedules[0] &&
                (selectedDayParts.length < 1
                  ? true
                  : selectedDayParts.includes(
                      schedules[0]?.dayPart ?? 'MORNING'
                    ))
            )
            ?.map((props) => {
              const schedule = props.schedules[0];

              return (
                <ActivityPanelPragmatic
                  key={schedule?.appointmentGrouping?.entityId}
                  activeEntityContainingSelection={
                    activeEntityContainingSelection
                  }
                  readOnly={!hasActivityPlanningEditAccess}
                  showEmployeeName
                  onConnectClick={() => {
                    if (schedule?.appointmentGrouping) {
                      connectAppointmentGrouping(
                        schedule.appointmentGrouping!.entityId
                      );
                    }
                  }}
                  onChangeSelection={onChangeSelection}
                  onChangeActiveEntity={onChangeActiveEntity}
                  {...schedule}
                  headerName={schedule.name}
                  events={(schedule?.events as Event[]) ?? []}
                  planningEntityType={props.type}
                  planningEntityId={props.id}
                  planningEntityIdentifier={props.planningEntityIdentifier}
                  isColumnLoading={loadingEntityIds.has(props.id)}
                  isOverrulingExternalDuty={schedule.isOverrulingExternalDuty}
                />
              );
            })}
      </PanelGrid>
    </Container>
  );
}

export default RouteOverviewPragmatic;
